import {createRule, type Maybe} from '@regle/core';
import {isFilled} from '@regle/rules';
import {useTranslation} from '#imports';
import {
    checkTINByCountry,
    getTINExampleByCountryAndLegalPersonType,
    getTINTypeByCountryAndLegalPersonType,
} from './taxIdentificationNumber.validator';

/**
 * Check if a tax identification number is valid for the country provided
 *
 * @param countryCode the country you want to check
 * @param legalPersonType the legal person type (SOLETRADER, BUSINESS, ORGANIZATION) — used to show a more specific error message
 *
 * @example
 * ```ts
 *  import { useRegle, taxIdentificationNumber } from '@malt/forms';
 *
 * const countryCode = ref('FR');
 * const legalPersonType = ref('SOLETRADER');
 *
 *  const { r$ } = useRegle(... , {
 *    taxIdNumber: { taxIdentificationNumber: taxIdentificationNumber(countryCode, legalPersonType) }
 *  })
 * ```
 * */
export const taxIdentificationNumber = createRule({
    type: 'taxIdentificationNumber',
    validator(value: Maybe<string>, countryCode: string, legalPersonType?: string) {
        if (isFilled(value)) {
            return checkTINByCountry(value, countryCode);
        }
        return true;
    },
    message: ({$params: [countryCode, legalPersonType]}) => {
        const {t} = useTranslation();
        const example = getTINExampleByCountryAndLegalPersonType(countryCode, legalPersonType) ?? '';
        const type = getTINTypeByCountryAndLegalPersonType(countryCode, legalPersonType) ?? '';
        if (example && type) {
            return t('generic.forms.validation.errors.taxIdentificationNumber.format', [example, type]);
        }
        return t('generic.forms.validation.errors.taxIdentificationNumber');
    },
});
